<script lang="ts">
	/**
	 * The block every page opens with: an optional breadcrumb trail, the page
	 * title with its accent underline, and an optional description.
	 *
	 * The underline is drawn by `.pageHeader::after` in `app.css`. The inner
	 * `h1` is the page's real heading — `app.css` suppresses its own `h1::after`
	 * inside a `.pageHeader` so the rule isn't drawn twice.
	 *
	 * `title` / `description` are the convenience form; the matching snippets
	 * take over when the heading needs markup rather than plain text.
	 */

	import { Breadcrumb, P, Popover } from 'flowbite-svelte';
	import type { Snippet } from 'svelte';
	import RoutedBreadcrumbItem from './RoutedBreadcrumbItem.svelte';
	import { t } from '../i18n';

	interface Props {
		/** Page title. Equivalent to filling the `header` snippet with plain text. */
		title?: string;
		/** Subtitle shown below the title. Equivalent to the `description` snippet. */
		description?: string;
		/** Breadcrumb trail. The last item renders unlinked, as the current page. */
		breadcrumbs?: Array<{ label: string; href?: string }>;
		/** Rich page title, when plain text isn't enough. */
		header?: Snippet;
		/** Rich description, when plain text isn't enough. */
		descriptionSnippet?: Snippet;
		/** Contents of the `?` popover beside the title. Omit for no popover. */
		help?: Snippet;
	}

	let {
		title = '',
		description = '',
		breadcrumbs = [],
		header,
		descriptionSnippet,
		help
	}: Props = $props();
</script>

{#if breadcrumbs.length > 0}
	<Breadcrumb class="mb-2">
		<RoutedBreadcrumbItem home href="/">{$t('Dashboard')}</RoutedBreadcrumbItem>
		{#each breadcrumbs as crumb, i (crumb.label)}
			{#if crumb.href && i < breadcrumbs.length - 1}
				<RoutedBreadcrumbItem href={crumb.href}>{crumb.label}</RoutedBreadcrumbItem>
			{:else}
				<RoutedBreadcrumbItem>{crumb.label}</RoutedBreadcrumbItem>
			{/if}
		{/each}
	</Breadcrumb>
{/if}

<div class="pageHeader mb-6 text-2xl font-bold">
	<div class="flex items-center">
		<h1 class="mr-4 flex-none text-2xl font-bold text-gray-900 dark:text-white">
			{#if header}{@render header()}{:else}{title}{/if}
		</h1>
		<div class="grow"></div>
		{#if help}
			<div class="text-2xl">
				<div class="cursor-help text-blue-600 hover:text-primary-500 dark:text-blue-400">
					<i class="fa-solid fa-circle-question"></i>
				</div>
				<Popover
					class="z-30 w-96 text-base font-normal shadow-2xl shadow-blue-800"
					placement="bottom"
				>
					{@render help()}
				</Popover>
			</div>
		{/if}
	</div>
</div>

{#if descriptionSnippet || description}
	<P class="w-full pb-4 text-base leading-normal font-semibold text-gray-600 empty:pb-0 dark:text-gray-400">
		{#if descriptionSnippet}{@render descriptionSnippet()}{:else}{description}{/if}
	</P>
{/if}
